"use client";
import { useEffect, useState } from 'react';
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';

const RELEASE_NOTE_KEY = 'whatsnew.seenRelease';
const LEGACY_VERSION_KEY = 'whatsnew.version';
const RELEASE_NOTE = {
  id: '2026-05-agent-ui-qa-toast',
  title: 'What’s new',
  summary: 'Faster chat rendering, Focus Mode, compact sidebar, unread indicators, and keyboard shortcuts.',
};

export default function WhatsNewBanner() {
  const isE2ETest = process.env.NEXT_PUBLIC_E2E_TEST === '1';
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isE2ETest) return;
    let seenTimer: ReturnType<typeof window.setTimeout> | undefined;
    let hideTimer: ReturnType<typeof window.setTimeout> | undefined;
    try {
      const seenRelease = localStorage.getItem(RELEASE_NOTE_KEY);
      const legacySeenRelease = localStorage.getItem(LEGACY_VERSION_KEY);
      const shouldShow = seenRelease !== RELEASE_NOTE.id && legacySeenRelease !== RELEASE_NOTE.id;
      setVisible(shouldShow);
      if (shouldShow) {
        seenTimer = window.setTimeout(() => {
          try { localStorage.setItem(RELEASE_NOTE_KEY, RELEASE_NOTE.id); } catch {}
        }, 2500);
        hideTimer = window.setTimeout(() => {
          try { localStorage.setItem(RELEASE_NOTE_KEY, RELEASE_NOTE.id); } catch {}
          setVisible(false);
        }, 12000);
      }
    } catch {
      setVisible(true);
    }
    return () => {
      if (seenTimer) window.clearTimeout(seenTimer);
      if (hideTimer) window.clearTimeout(hideTimer);
    };
  }, [isE2ETest]);

  const dismiss = () => {
    try { localStorage.setItem(RELEASE_NOTE_KEY, RELEASE_NOTE.id); } catch {}
    setVisible(false);
  };

  if (isE2ETest || !visible) return null;

  return (
    <div className="pointer-events-none fixed inset-x-4 bottom-[calc(env(safe-area-inset-bottom)+5rem)] z-[90] md:inset-x-auto md:bottom-auto md:left-20 md:top-3 md:w-[min(24rem,calc(100vw-6rem))]">
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-auto rounded-xl border border-border bg-card/95 p-3 shadow-xl backdrop-blur"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/15">
            <SparklesIcon className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-foreground">{RELEASE_NOTE.title}</div>
            <div className="text-sm leading-5 text-muted-foreground">
              {RELEASE_NOTE.summary} Press ? to view.
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={dismiss}
            aria-label="Dismiss what’s new"
            className="-mr-1 -mt-1 shrink-0"
          >
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
